export type ProjectRenderLighting = "day" | "sunny" | "evening";

export type RenderQuotaReason =
  | "available"
  | "order_required"
  | "approval_pending"
  | "approval_rejected"
  | "exhausted"
  | "daily_limit"
  | "service_limit"
  | "busy";

export type RenderQuota = {
  freeLimit: number;
  bonusLimit: number;
  used: number;
  total: number;
  remaining: number;
  orderSubmitted: boolean;
  approvalStatus: "none" | "pending" | "approved" | "rejected";
  canRender: boolean;
  reason: RenderQuotaReason;
  /** Вошёл сотрудник — рендеры без лимита. */
  staff?: boolean;
};

type RoomRenderRequest = {
  roomPhoto: File;
  projectImage: string;
  projectName: string;
  projectSpecification: string;
  moduleCount: number;
  roomAspectRatio: number;
};

type ProjectRenderRequest = {
  projectImage: string;
  projectReferenceImage?: string | null;
  projectName: string;
  projectSpecification: string;
  lighting: ProjectRenderLighting;
};

const responseError = async (response: Response) => {
  try {
    const body = (await response.json()) as { error?: string };
    return body.error || `Ошибка сервера ${response.status}`;
  } catch {
    return `Ошибка сервера ${response.status}`;
  }
};

const dataUrlToBlob = async (dataUrl: string) => {
  const response = await fetch(dataUrl);
  if (!response.ok) {
    throw new Error("Не удалось подготовить изображение проекта.");
  }
  return response.blob();
};

const resultBlob = async (response: Response) => {
  if (!response.ok) {
    throw new Error(await responseError(response));
  }

  const result = await response.blob();
  if (!result.type.startsWith("image/")) {
    throw new Error("Сервис вернул неизвестный формат результата.");
  }
  return result;
};

const quotaResult = async (response: Response) => {
  const result = (await response.json().catch(() => null)) as
    | { ok?: boolean; error?: string; quota?: RenderQuota }
    | null;
  if (!response.ok || !result?.ok || !result.quota) {
    throw new Error(result?.error || `Ошибка сервера ${response.status}`);
  }
  return result.quota;
};

export async function getRenderQuota() {
  const response = await fetch("/api/renders/quota", {
    cache: "no-store"
  });
  return quotaResult(response);
}

export async function unlockRenderQuota(token: string) {
  const response = await fetch("/api/renders/unlock", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token })
  });
  return quotaResult(response);
}

export async function syncRenderOrderStatus(token: string) {
  const response = await fetch("/api/renders/order-status", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token })
  });
  return quotaResult(response);
}

export async function createProjectRender({
  projectImage,
  projectReferenceImage,
  projectName,
  projectSpecification,
  lighting
}: ProjectRenderRequest) {
  const projectBlob = await dataUrlToBlob(projectImage);
  const form = new FormData();
  form.append("projectImage", projectBlob, "egel-project.jpg");
  if (projectReferenceImage) {
    form.append(
      "projectReferenceImage",
      await dataUrlToBlob(projectReferenceImage),
      "egel-project-reference.png"
    );
  }
  form.append("projectName", projectName);
  form.append("projectSpecification", projectSpecification);
  form.append("lighting", lighting);

  const response = await fetch("/api/renders/project", {
    method: "POST",
    body: form
  });

  return resultBlob(response);
}

export async function createRoomRender({
  roomPhoto,
  projectImage,
  projectName,
  projectSpecification,
  moduleCount,
  roomAspectRatio
}: RoomRenderRequest) {
  const projectBlob = await dataUrlToBlob(projectImage);
  const form = new FormData();
  form.append("roomPhoto", roomPhoto, roomPhoto.name || "room.jpg");
  form.append("projectImage", projectBlob, "egel-complete-project.jpg");
  form.append("projectName", projectName);
  form.append("projectSpecification", projectSpecification);
  form.append("moduleCount", String(moduleCount));
  form.append("roomAspectRatio", String(roomAspectRatio));

  const response = await fetch("/api/renders/room", {
    method: "POST",
    body: form
  });

  return resultBlob(response);
}
